import React, { useState } from 'react';
import InfoTextPreview, { infoTextHelperText } from './InfoTextPreview';
import { HELP_TEXT_MAX_LENGTH } from '@shared/help-text';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { AgronodTypography, Tooltip } from '@agronod/mui-components';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import {
  DEFAULT_PERIOD_CONFIG,
  formatYearList,
  requirementYears,
  resolveControlPeriod,
  type ControlPeriodConfig,
  type CoverageRule,
} from '@shared/control-period';
import { toDateOnlyString } from '@/utils/format';
import { previewPhrase } from './ControlPeriodSelect';
import type { CategoryValidityMode, DocumentRequirement } from '@/types';
import type { FilterRule } from '@/types/farm-profile';
import { handleApiError } from '@/services/api';
import { AdminService } from '@/services/admin.service';
import { useSnackbar } from 'notistack';
import FilterRuleEditor from './FilterRuleEditor';
import TagChips from '@/components/tags/TagChips';
import TagPicker from '@/components/tags/TagPicker';
import ValidityModeSelect from './ValidityModeSelect';

interface DocumentRequirementEditorProps {
  requirements: DocumentRequirement[];
  onChange: (requirements: DocumentRequirement[]) => void;
  disabled?: boolean;
  controlTypeId: string; // Required for API calls
  /** The control type's period model; drives the year-window preview. */
  periodConfig?: ControlPeriodConfig;
  /** The control type's sections (Sektioner tab); a requirement is filed under one by title. */
  sections?: ReadonlyArray<{ id: string; title: string }>;
}

/** The Select value for "no section" — an empty string is what MUI's Select expects for it. */
const NO_SECTION = '';

/** Offsets an admin can toggle: 0 is the period's main year, -1 the year before, … */
const OFFSET_CHOICES = [0, -1, -2, -3, -4, -5] as const;

const offsetLabel = (offset: number): string => (offset === 0 ? 'Kontrollåret' : `−${-offset}`);

/** Newest first (0, -1, -2 …) so the wire value does not depend on click order. */
const sortOffsets = (offsets: readonly number[]): number[] => [...offsets].sort((a, b) => b - a);

/** "Årsfönster: 0, −1, −2 · alla år" for the requirement list. */
function yearWindowSummary(offsets: readonly number[], rule: CoverageRule): string {
  if (offsets.length === 0) return 'Inget årsfönster';
  const list = sortOffsets(offsets)
    .map((offset) => (offset === 0 ? '0' : `−${-offset}`))
    .join(', ');
  return `Årsfönster: ${list} · ${rule === 'ANY_YEAR' ? 'något av åren' : 'alla år'}`;
}

/**
 * Live preview of what the year window means for a control dated today, using
 * the control type's period model. Returns null when the period cannot be
 * resolved (rolling type without months).
 */
function yearWindowPreview(
  config: ControlPeriodConfig,
  offsets: readonly number[],
  rule: CoverageRule,
): string | null {
  const today = toDateOnlyString(new Date());
  let mainYear: number;
  let periodText: string;
  try {
    const period = resolveControlPeriod(config, today, today);
    mainYear = period.mainYear;
    periodText = previewPhrase(period);
  } catch {
    return null;
  }
  const prefix = `Kontroll ${today} → ${periodText}`;
  if (offsets.length === 0) return `${prefix} → Inget årsfönster – inga år visas som saknade`;
  const years = formatYearList(requirementYears(mainYear, offsets));
  return `${prefix} → kravet omfattar ${years}, ${rule === 'ANY_YEAR' ? 'något av åren' : 'alla år'}`;
}

interface RequirementFormData {
  /** Tag slugs the requirement asks for. Never empty — the API rejects that. */
  tags: string[];
  /** Validity pre-selected when the farmer uploads here; null = no pre-selection. */
  validityMode: CategoryValidityMode | null;
  isRequired: boolean;
  /** null = no limit. Sent as null so the backend clears the column; an
   *  omitted field would be read as "unchanged" (AG-262). */
  validityPeriod?: number | null;
  /** Annual requirements only: offsets from the period's main year (0, -1, …). */
  yearOffsets: number[];
  coverageRule: CoverageRule;
  description: string;
  informationText?: string;
  sectionName?: string;
  displayOrder?: number;
  /**
   * Two states now that nothing is inherited (AG-249):
   * - null                  → no filter, the requirement always shows
   * - { conditions: [...] } → the rule that decides who sees it
   * The `{ conditions: [] }` sentinel still normalises to "no filter".
   */
  filterRule: FilterRule | null;
}

const emptyRequirement: RequirementFormData = {
  tags: [],
  validityMode: null,
  isRequired: true,
  yearOffsets: [],
  coverageRule: 'ALL_YEARS',
  description: '',
  displayOrder: 0,
  filterRule: null,
};

/** Returns true when rule encodes the explicit "no filter" sentinel. */
function isExplicitNoFilter(rule: FilterRule | null): boolean {
  return rule !== null && rule.conditions.length === 0;
}

const DocumentRequirementEditor: React.FC<DocumentRequirementEditorProps> = ({
  requirements,
  onChange,
  disabled = false,
  controlTypeId,
  periodConfig = DEFAULT_PERIOD_CONFIG,
  sections = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formOpen, setFormOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<DocumentRequirement | null>(null);
  const [formData, setFormData] = useState<RequirementFormData>(emptyRequirement);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFormOpen = (requirement?: DocumentRequirement) => {
    if (requirement) {
      setEditingRequirement(requirement);
      const formDataObj: RequirementFormData = {
        tags: requirement.tags ?? [],
        validityMode: requirement.validityMode ?? null,
        isRequired: requirement.isRequired,
        yearOffsets: requirement.yearOffsets,
        coverageRule: requirement.coverageRule,
        description: requirement.description,
        displayOrder: requirement.displayOrder ?? 0,
        filterRule: requirement.filterRule ?? null,
      };
      if (requirement.informationText !== undefined) {
        formDataObj.informationText = requirement.informationText;
      }
      if (requirement.validityPeriod !== undefined) {
        formDataObj.validityPeriod = requirement.validityPeriod;
      }
      if (requirement.sectionName !== undefined) {
        formDataObj.sectionName = requirement.sectionName;
      }
      setFormData(formDataObj);
    } else {
      setEditingRequirement(null);
      setFormData(emptyRequirement);
    }
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingRequirement(null);
    setFormData(emptyRequirement);
  };

  const handleFormSubmit = async () => {
    if (formData.tags.length === 0) {
      // An untagged requirement would accept every document, including
      // untagged ones — the API rejects it, but say so before the round trip.
      setError('Kravet måste ha minst en tagg');
      return;
    }
    if (!formData.description.trim()) {
      setError('Beskrivning måste fyllas i');
      return;
    }

    // Same tag set AND description already present (unless editing that one).
    const formTagKey = [...formData.tags].sort().join('|');
    const existingRequirement = requirements.find(req => {
      const isSameTags = [...(req.tags ?? [])].sort().join('|') === formTagKey;
      const isSameDescription = req.description.trim().toLowerCase() === formData.description.trim().toLowerCase();
      const isDifferentRequirement = req.id !== editingRequirement?.id;
      return isSameTags && isSameDescription && isDifferentRequirement;
    });

    if (existingRequirement) {
      setError('Ett krav med samma taggar och beskrivning finns redan');
      return;
    }

    setSaving(true);
    setError(null);

    const isAnnual = formData.validityMode === 'annual';
    const isExpiring = formData.validityMode === 'expires';

    try {
      const requirementData = {
        tags: formData.tags,
        // Always present: null clears the pre-selection server-side.
        validityMode: formData.validityMode,
        description: formData.description.trim(),
        isRequired: formData.isRequired,
        displayOrder: formData.displayOrder ?? 0,
        ...(formData.informationText?.trim() && { informationText: formData.informationText.trim() }),
        // Always present: null clears the limit server-side, where a missing
        // field means "unchanged" and would keep the old value.
        // Months only mean something for 'expires'; any other mode clears them.
        validityPeriod: isExpiring ? (formData.validityPeriod ?? null) : null,
        // Only annual requirements have a year window; other modes clear it.
        yearOffsets: isAnnual ? sortOffsets(formData.yearOffsets) : [],
        coverageRule: isAnnual ? formData.coverageRule : ('ALL_YEARS' as CoverageRule),
        ...(formData.sectionName?.trim() && { sectionName: formData.sectionName.trim() }),
        filterRule: formData.filterRule,
      };

      if (editingRequirement) {
        // Update existing requirement via API
        await AdminService.updateDocumentRequirement(
          controlTypeId,
          editingRequirement.id,
          requirementData
        );
        enqueueSnackbar('Dokumentkrav uppdaterat', { variant: 'success' });

        // Update local state. The wire format's null becomes an absent field
        // here, which is how DocumentRequirement models "no limit".
        const { validityPeriod, ...savedFields } = requirementData;
        const updatedRequirements = requirements.map(req => {
          if (req.id !== editingRequirement.id) return req;
          const { validityPeriod: _previous, ...rest } = req;
          return {
            ...rest,
            ...savedFields,
            ...(validityPeriod !== null && { validityPeriod }),
            filterRule: formData.filterRule,
          };
        });
        onChange(updatedRequirements);
      } else {
        // Create new requirement via API
        const createdRequirement = await AdminService.createDocumentRequirement(
          controlTypeId,
          requirementData
        );
        enqueueSnackbar('Dokumentkrav skapat', { variant: 'success' });

        // Add to local state
        const newRequirement: DocumentRequirement = {
          id: createdRequirement.id,
          tags: createdRequirement.tags ?? [],
          validityMode: createdRequirement.validityMode ?? null,
          isRequired: createdRequirement.isRequired,
          yearOffsets: requirementData.yearOffsets,
          coverageRule: requirementData.coverageRule,
          description: createdRequirement.description,
          displayOrder: createdRequirement.displayOrder ?? 0,
          ...(createdRequirement.informationText && { informationText: createdRequirement.informationText }),
          ...(createdRequirement.validityPeriod && { validityPeriod: createdRequirement.validityPeriod }),
          ...(createdRequirement.sectionName && { sectionName: createdRequirement.sectionName }),
          filterRule: formData.filterRule,
        };
        onChange([...requirements, newRequirement]);
      }

      handleFormClose();
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (requirementId: string) => {
    if (!confirm('Är du säker på att du vill ta bort detta krav?')) {
      return;
    }

    try {
      await AdminService.deleteDocumentRequirement(controlTypeId, requirementId);
      enqueueSnackbar('Dokumentkrav borttaget', { variant: 'success' });

      const updatedRequirements = requirements.filter(req => req.id !== requirementId);
      onChange(updatedRequirements);
    } catch (err) {
      const errorMessage = handleApiError(err);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Derived values used inside the dialog — computed once per render.
  const hasExplicitNoFilter = isExplicitNoFilter(formData.filterRule);
  const filterConditionCount = formData.filterRule?.conditions.length ?? 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2
        }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Dokumentkrav
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            Konfigurera vilka dokument som krävs för denna kontrolltyp. Krav används för
            automatisk matchning av dokument och validering.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleFormOpen()}
          disabled={disabled}
          size="small"
        >
          Lägg till krav
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {requirements.length === 0 ? (
        <Alert severity="info">
          Inga dokumentkrav har lagts till ännu. Klicka på "Lägg till krav" för att börja.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent sx={{ p: 0 }}>
            {/* Ordering lives in the Sortering tab, where documents and form fields
                share one sequence. This table is a flat list on purpose. */}
            <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Kategori</TableCell>
                          <TableCell>Beskrivning</TableCell>
                          <TableCell>Typ</TableCell>
                          <TableCell>Giltighet</TableCell>
                          <TableCell width="100px">Åtgärder</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requirements.map((requirement) => (
                              <TableRow key={requirement.id}>
                                <TableCell>
                                  <Box>
                                    <TagChips slugs={requirement.tags ?? []} />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontFamily: "monospace"
                                      }}>
                                      {(requirement.tags ?? []).join(', ')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {requirement.description}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={requirement.isRequired ? 'Obligatorisk' : 'Valfri'}
                                    color={requirement.isRequired ? 'error' : 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{
                                    color: "text.secondary"
                                  }}>
                                    {requirement.validityMode === 'annual'
                                      ? yearWindowSummary(requirement.yearOffsets, requirement.coverageRule)
                                      : requirement.validityMode === 'expires' && requirement.validityPeriod
                                        ? `${requirement.validityPeriod} månader`
                                        : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5
                                    }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleFormOpen(requirement)}
                                      disabled={disabled}
                                      title="Redigera krav"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(requirement.id)}
                                      disabled={disabled}
                                      title="Ta bort krav"
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRequirement ? 'Redigera dokumentkrav' : 'Lägg till dokumentkrav'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TagPicker
                  value={formData.tags}
                  onChange={(tags) => setFormData({ ...formData, tags })}
                  label="Taggar"
                  error={formData.tags.length === 0}
                  helperText="Ett dokument uppfyller kravet när det bär alla dessa taggar. Färre taggar ger ett bredare krav."
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  }}>
                  <Typography variant="body2">
                    Kravet är:
                  </Typography>
                  <Tooltip title="Räknas bara för gårdar där kravet gäller enligt filterregel, och kan markeras Ej aktuellt av lantbrukaren.">
                    <HelpIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    />
                  }
                  label={formData.isRequired ? 'Obligatorisk (om tillämplig)' : 'Valfri'}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <ValidityModeSelect
                  value={formData.validityMode}
                  onChange={(validityMode) => setFormData({ ...formData, validityMode })}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Beskrivning"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Beskriv vad detta dokumentkrav innebär och vad som förväntas..."
                  helperText="Ange en tydlig beskrivning som hjälper användare att förstå vad som krävs"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Informationstext (valfritt)"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.informationText || ''}
                  onChange={(e) => setFormData({ ...formData, informationText: e.target.value })}
                  placeholder="Detaljerad vägledning för användare om detta dokumentkrav..."
                  helperText={infoTextHelperText(formData.informationText)}
                  slotProps={{ htmlInput: { maxLength: HELP_TEXT_MAX_LENGTH } }}
                />
                <InfoTextPreview text={formData.informationText || ''} />
              </Grid>

              {formData.validityMode === 'expires' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Giltighet (månader)"
                    type="number"
                    fullWidth
                    value={formData.validityPeriod ?? ''}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        validityPeriod: e.target.value ? parseInt(e.target.value) : null,
                      });
                    }}
                    helperText="Hur länge ett dokument är giltigt från sitt datum. Lämna tomt för ingen begränsning."
                    slotProps={{
                      htmlInput: { min: 1, max: 120 }
                    }}
                  />
                </Grid>
              )}

              {formData.validityMode === 'annual' && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Årsfönster
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {/* A migrated window may reach further back than the fixed
                        choices (84 months → −6); the stored offset gets a chip
                        too, or the admin could neither see nor remove it. */}
                    {sortOffsets(Array.from(new Set([...OFFSET_CHOICES, ...formData.yearOffsets]))).map((offset) => {
                      const selected = formData.yearOffsets.includes(offset);
                      return (
                        <Chip
                          key={offset}
                          label={offsetLabel(offset)}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          aria-pressed={selected}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              yearOffsets: sortOffsets(
                                selected
                                  ? formData.yearOffsets.filter((o) => o !== offset)
                                  : [...formData.yearOffsets, offset],
                              ),
                            })
                          }
                        />
                      );
                    })}
                  </Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={formData.coverageRule}
                    onChange={(_, rule: CoverageRule | null) => {
                      // Exclusive groups report null when the active button is clicked again.
                      if (rule) setFormData({ ...formData, coverageRule: rule });
                    }}
                    aria-label="Täckningsregel"
                  >
                    <ToggleButton value="ALL_YEARS">Alla år måste finnas</ToggleButton>
                    <ToggleButton value="ANY_YEAR">Något av åren räcker</ToggleButton>
                  </ToggleButtonGroup>
                  <AgronodTypography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    Kontrollåret är kontrollperiodens huvudår; −1 är året före, och så vidare. Utan valda år visas inga år som saknade.
                  </AgronodTypography>
                  <AgronodTypography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    {yearWindowPreview(periodConfig, formData.yearOffsets, formData.coverageRule)}
                  </AgronodTypography>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 6 }}>
                {/* Same control as a form field's section: the requirement is
                    filed under one of the control type's sections, not under a
                    free-text name that may differ from them by a letter. A
                    stored name that is no longer a section stays selectable so
                    the requirement can be opened and moved. */}
                <FormControl fullWidth>
                  <InputLabel id="requirement-section-label">Sektion (valfritt)</InputLabel>
                  <Select
                    labelId="requirement-section-label"
                    label="Sektion (valfritt)"
                    value={formData.sectionName ?? NO_SECTION}
                    onChange={(e) => {
                      const { sectionName: _, ...rest } = formData;
                      const title = e.target.value;
                      setFormData(title === NO_SECTION ? rest : { ...rest, sectionName: title });
                    }}
                  >
                    <MenuItem value={NO_SECTION}>
                      <em>Ingen sektion</em>
                    </MenuItem>
                    {sections.map((section) => (
                      <MenuItem key={section.id} value={section.title}>
                        {section.title}
                      </MenuItem>
                    ))}
                    {formData.sectionName && !sections.some((s) => s.title === formData.sectionName) && (
                      <MenuItem value={formData.sectionName}>
                        {formData.sectionName} (finns inte under Sektioner)
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>
                    Sektioner skapas under fliken Sektioner och ordnas under Sortering
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Accordion disabled>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{
                      color: "text.secondary"
                    }}>
                      Automatiska matchningsregler (Kommer i framtida version)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>
                      Här kommer du kunna konfigurera regler för automatisk matchning av dokument
                      baserat på metadata, datum, filnamn och andra kriterier.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                      <Typography variant="subtitle2">
                        Filterregel
                      </Typography>
                      {/* Show badge for active own conditions */}
                      {filterConditionCount > 0 && (
                        <Chip
                          label={`${filterConditionCount} villkor`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {/* Show badge when explicit no-filter sentinel is active */}
                      {hasExplicitNoFilter && (
                        <Chip
                          label="Filtrering av"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Explicit no-filter indicator */}
                      {hasExplicitNoFilter && (
                        <Alert severity="warning" icon={false}>
                          <Typography variant="body2">
                            Filtrering avstängd — kravet visas för alla gårdar.
                          </Typography>
                        </Alert>
                      )}

                      <FilterRuleEditor
                        value={formData.filterRule}
                        onChange={(next) => setFormData({ ...formData, filterRule: next })}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFormClose} disabled={saving}>
            Avbryt
          </Button>
          <Button variant="contained" onClick={handleFormSubmit} disabled={saving}>
            {saving ? 'Sparar...' : editingRequirement ? 'Uppdatera' : 'Lägg till'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentRequirementEditor;
